import { GatewayInfoSocieteOrias } from "@conformite/gateway";
import { zod } from "@endya-conformite/ui-kit/src/lya/exports";
import {
  Form,
  InputButtonGroup,
  InputSelect,
  useFormConfig,
  Card,
  FormField,
  FormRow,
} from "@endya-conformite/ui-kit";
import { ParcoursAction } from "@src/parcours/NavigationButtons/ParcoursAction";
import { ParcoursFullPageTemplate } from "@src/parcours/Template/ParcoursFullPageTemplate";
import { isCategorieAccessoire } from "@src/payment/utils/createIntentionPaiement";
import {
  ID_FORM_RENOUVELLEMENT,
  ParcoursRenouvellementStepComponentProps,
  RenouvellementStepEnum,
} from "@src/renouvellement/ParcoursRenouvellement.definition";
import { categorieRepresentationOptions } from "@src/renouvellement/steps/informationsComplementaires/RenouvellementInformationsComplementairesForm";
import {
  categorieActiviteLabel,
  InputTitreAccessoire,
} from "@src/societes/form/EntrepriseInfoActiviteForm";
import style from "@src/societes/form/EntrepriseInfoActiviteForm.module.scss";
import { partition } from "lodash";
import { useCallback, useEffect, useMemo } from "react";

const combinedSchema = zod.object({
  numeroOrias: zod.string().optional(),
  statutOrias: zod.nativeEnum(GatewayInfoSocieteOrias.StatutSociete),
  categories: zod
    .array(zod.nativeEnum(GatewayInfoSocieteOrias.Categorie))
    .min(1, {
      message:
        "Vous devez choisir au moins une catégorie à laquelle vous inscrire",
    }),
  coaAccessoire: zod.boolean(),
  miaAccessoire: zod.boolean(),
  cobspAccessoire: zod.boolean(),
  miobspAccessoire: zod.boolean(),
  categorieRepresentation: zod.nativeEnum(
    GatewayInfoSocieteOrias.CategorieRepresentation,
    {
      errorMap: () => ({
        message: "Veuillez sélectionner votre catégorie de représentation",
      }),
    }
  ),
});

type CombinedFormValues = zod.infer<typeof combinedSchema>;

const labelByStatutSociete: Record<
  GatewayInfoSocieteOrias.StatutSociete,
  string
> = {
  [GatewayInfoSocieteOrias.StatutSociete.INSCRIT]: "Inscrit",
  [GatewayInfoSocieteOrias.StatutSociete.JAMAIS_INSCRIT]: "Non inscrit",
  [GatewayInfoSocieteOrias.StatutSociete.SUPPRIME]: "Supprimé",
};

const ACCESSOIRE_CATEGORIES = [
  {
    categorie: GatewayInfoSocieteOrias.Categorie.COA,
    field: "coaAccessoire",
  },
  {
    categorie: GatewayInfoSocieteOrias.Categorie.MIA,
    field: "miaAccessoire",
  },
  {
    categorie: GatewayInfoSocieteOrias.Categorie.COBSP,
    field: "cobspAccessoire",
  },
  {
    categorie: GatewayInfoSocieteOrias.Categorie.MIOBSP,
    field: "miobspAccessoire",
  },
] as const;

export function RenouvellementInfoActivitePage({
  data,
  setData,
  changeStep,
}: ParcoursRenouvellementStepComponentProps) {
  const entreprise =
    data.entreprisesAControler[data.resume.entreprisePrincipale.siren];

  const formConfig = useFormConfig<typeof combinedSchema, CombinedFormValues>({
    schema: combinedSchema,
    defaultValues: {
      numeroOrias: entreprise.numeroOrias,
      statutOrias: entreprise.numeroOrias
        ? GatewayInfoSocieteOrias.StatutSociete.INSCRIT
        : GatewayInfoSocieteOrias.StatutSociete.JAMAIS_INSCRIT,
      categories: [
        ...entreprise.categoriesActivites,
        ...entreprise.categoriesActivitesAccessoires,
      ],
      ...Object.fromEntries(
        ACCESSOIRE_CATEGORIES.map(({ categorie, field }) => [
          field,
          entreprise.categoriesActivitesAccessoires.includes(categorie),
        ])
      ),
      categorieRepresentation: entreprise.categorieRepresentation,
    },
  });

  const categoriesWatched = formConfig.watch("categories");
  const numeroOrias = formConfig.watch("numeroOrias");
  const statutOrias = formConfig.watch("statutOrias");

  const categoriesOptions = useMemo(
    () =>
      ACCESSOIRE_CATEGORIES.map(({ categorie, field }) => ({
        value: categorie,
        label: categorieActiviteLabel[categorie],
        description: categoriesWatched?.includes(categorie) ? (
          <InputTitreAccessoire name={field} />
        ) : undefined,
      })),
    [categoriesWatched]
  );

  useEffect(() => {
    ACCESSOIRE_CATEGORIES.forEach(({ categorie, field }) => {
      if (!categoriesWatched?.includes(categorie)) {
        formConfig.setValue(field, false);
      }
    });
  }, [categoriesWatched, formConfig]);

  const handleSubmit = useCallback(
    (values: CombinedFormValues) => {
      const [categorieAccessoire, categorieNonAccessoire] = partition(
        values.categories,
        (categorie) =>
          isCategorieAccessoire(categorie, {
            coaAccessoire: !!values.coaAccessoire,
            miaAccessoire: !!values.miaAccessoire,
            cobspAccessoire: !!values.cobspAccessoire,
            miobspAccessoire: !!values.miobspAccessoire,
          })
      );

      setData({
        ...data,
        entreprisesAControler: {
          ...data.entreprisesAControler,
          [entreprise.siren]: {
            ...entreprise,
            categoriesActivites: categorieNonAccessoire,
            categoriesActivitesAccessoires: categorieAccessoire,
            categorieRepresentation: values.categorieRepresentation,
          },
        },
      });
      changeStep(RenouvellementStepEnum.SOUS_CATEGORIES);
      if (
        categorieNonAccessoire.find((categorie) =>
          [
            GatewayInfoSocieteOrias.Categorie.COBSP,
            GatewayInfoSocieteOrias.Categorie.MIOBSP,
          ].includes(categorie)
        )
      )
        changeStep(RenouvellementStepEnum.SOUS_CATEGORIES);
      else changeStep(RenouvellementStepEnum.ORGANISATIONS_PROFESSIONNELLES);
    },
    [setData, data, entreprise, changeStep]
  );

  return (
    <ParcoursFullPageTemplate
      title="Information activité"
      actions={
        <ParcoursAction type="submit" formId={ID_FORM_RENOUVELLEMENT}>
          Suivant
        </ParcoursAction>
      }
    >
      <Form
        formConfig={formConfig}
        onSubmit={handleSubmit}
        id={ID_FORM_RENOUVELLEMENT}
      >
        <Card variant="accent" className={style.informationsOrias}>
          <div>
            <span>Numéro ORIAS : </span>
            <strong className={style.value}>{numeroOrias ?? "-"}</strong>
          </div>

          <div>
            <span>Statut ORIAS : </span>
            <strong className={style.value}>
              {labelByStatutSociete[statutOrias]}
            </strong>
          </div>
        </Card>
        <FormRow>
          <FormField>
            <InputButtonGroup<
              CombinedFormValues,
              true,
              GatewayInfoSocieteOrias.Categorie
            >
              label={
                <b className={style.label}>
                  Dans quelle(s) catégories(s) vous inscrivez-vous ?
                </b>
              }
              options={categoriesOptions}
              name="categories"
              variant="outline-pop"
              size="large"
              isMultiple
            />
          </FormField>
        </FormRow>
        <FormRow>
          <FormField>
            <InputSelect<
              CombinedFormValues,
              GatewayInfoSocieteOrias.CategorieRepresentation
            >
              placeholder="Sélectionner..."
              options={categorieRepresentationOptions}
              name="categorieRepresentation"
              label="Catégorie de représentation"
              required
            />
          </FormField>
        </FormRow>
      </Form>
    </ParcoursFullPageTemplate>
  );
}
